/**
 * The gathering cover — the event cover's geometry (`EventCover`, the one the event, Pro and
 * club pages draw) with the gathering's identity in the text column. The page builds that
 * column (`identity`) so copy stays in GatheringPage; this component owns the photos, the
 * fade, the EDIT COVER toggle and the no-photo state.
 *
 *   desktop  radius 28, 480px (cover ≥ 1100 wide) else 440, text column 40px padding
 *   phone    photos only, 220px radius 22; the page renders the identity card below it
 *
 * `editing` turns every tile into a drag target (focal points through `onFocal`).
 */
import React from 'react';
import EventCover from '../event/EventCover';
import { posterPlaceholder } from '../../utils/posterPlaceholder';
import { RING_ON } from '../surface/SurfaceStyleContext';

const MUTED = '#6B7280';
const FAINT = '#9CA3AF';
const heightFor = (W, layout, mobile) => (mobile ? 220 : W >= 1100 ? 480 : 440);
const label = (size, spacing = '0.16em') => ({ fontSize: size, fontWeight: 700, letterSpacing: spacing, textTransform: 'uppercase' });

function EditButton({ open, onClick, phone }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={open} data-testid="edit-cover"
      className="absolute flex items-center rounded-full font-mono uppercase"
      style={{ right: phone ? 10 : 16, top: phone ? 10 : 16, zIndex: 3, gap: 8, height: phone ? 36 : 40, padding: phone ? '0 14px' : '0 16px', border: '2px solid transparent', boxSizing: 'border-box', background: open ? RING_ON : '#fff', color: open ? '#fff' : '#000', fontSize: phone ? 9 : 10, fontWeight: 800, letterSpacing: '0.14em', cursor: 'pointer' }}>
      {!phone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M3 5h18v14H3zM3 15l5-5 4 4 3-3 6 6" /></svg>}
      {open ? 'EDITING COVER' : 'EDIT COVER'}
    </button>
  );
}

export default function GatheringCover({ gathering, identity, photos = [], layout, split, phone = false, host = false, editing = false, onToggleEdit, onFocal, onOpenPhoto, onRatio, width }) {
  const hasPhotos = photos.length > 0;
  const common = { photos, layout, split, editable: editing, onFocal, onOpenPhoto: editing ? undefined : onOpenPhoto, onRatio, heightFor, width, testId: 'gathering-cover' };

  if (phone) {
    return (
      <div className="relative overflow-hidden bg-white" style={{ borderRadius: 22, height: 220 }} data-testid="gathering-cover-mobile" data-layout={hasPhotos ? undefined : 'none'}>
        {hasPhotos
          ? <EventCover {...common} mobile />
          : <div className="flex h-full items-center justify-center" style={{ background: posterPlaceholder(gathering?.id || 'gathering'), ...label(10), color: '#fff' }} data-testid="mobile-poster">NO PHOTOS YET</div>}
        {host && <EditButton open={editing} onClick={onToggleEdit} phone />}
      </div>
    );
  }

  return (
    <div className="relative" data-testid="gathering-header">
      <EventCover {...common} className="rounded-[28px]" textPadding={40}>
        {() => identity}
      </EventCover>
      {!hasPhotos && (
        <div className="absolute flex flex-col items-center justify-center text-center" style={{ right: 32, top: 32, bottom: 32, width: '34%', borderRadius: 22, background: posterPlaceholder(gathering?.id || 'gathering'), gap: 8, padding: 24, boxSizing: 'border-box' }} data-testid="no-photos-strip">
          <span style={{ ...label(12), color: '#fff' }}>NO PHOTOS</span>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: '#fff', opacity: 0.85 }}>{host ? 'Add photos with EDIT COVER. Pick a layout and drag each one into frame.' : 'The host hasn’t added cover photos yet.'}</span>
        </div>
      )}
      {host && <EditButton open={editing} onClick={onToggleEdit} />}
    </div>
  );
}

export { MUTED as COVER_MUTED, FAINT as COVER_FAINT };
